import { Marker, Polygon } from '@react-google-maps/api';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import { TERRITORIES_API_URL } from '@/constants/apis';
import { configuration } from '@/constants/config';
import generateMarkerSvg from '@/constants/territories/icons';
import { apiManager } from '@/helpers/api.manager';
import { toast } from '@/hooks/ui/use-toast';
import { getTerritoryColor } from '@/lib/territories';
import logger from '@/services/logger.service';
import { useTerritoryStore } from '@/stores/useTerritoryStore';
import { useZorTerritorySettingsStore } from '@/stores/useZorTerritorySettingsStore';
import { Territory } from '@/types/territories/territory.type';

import { TerritoryStatsDialog } from './TerritoryStatsDialog';

interface TerritoriesBoardProps {
  zoom: number;
  bounds: {
    ne: {
      lat: number;
      lng: number;
    };
    sw: {
      lat: number;
      lng: number;
    };
  };
  onTerritoryClick?: (territory: Territory) => void;
}

type TerritoryBounds = TerritoriesBoardProps['bounds'];

const VIEWPORT_BUFFER_RATIO = 0.15;

const isEmptyBounds = (bounds: TerritoryBounds): boolean =>
  bounds.sw.lat === 0 &&
  bounds.ne.lat === 0 &&
  bounds.sw.lng === 0 &&
  bounds.ne.lng === 0;

const expandBounds = (bounds: TerritoryBounds): TerritoryBounds => {
  const latMargin =
    Math.abs(bounds.ne.lat - bounds.sw.lat) * VIEWPORT_BUFFER_RATIO;
  const lngMargin =
    Math.abs(bounds.ne.lng - bounds.sw.lng) * VIEWPORT_BUFFER_RATIO;

  return {
    ne: {
      lat: bounds.ne.lat + latMargin,
      lng: bounds.ne.lng + lngMargin,
    },
    sw: {
      lat: bounds.sw.lat - latMargin,
      lng: bounds.sw.lng - lngMargin,
    },
  };
};

const isTerritoryInsideBounds = (
  territory: Territory,
  bounds: TerritoryBounds
): boolean => {
  if (
    !territory.centroid ||
    !Array.isArray(territory.centroid.coordinates) ||
    territory.centroid.coordinates.length < 2
  ) {
    return false;
  }

  const [lng, lat] = territory.centroid.coordinates;

  return (
    lat >= bounds.sw.lat &&
    lat <= bounds.ne.lat &&
    lng >= bounds.sw.lng &&
    lng <= bounds.ne.lng
  );
};

const isRequestCanceled = (error: unknown): boolean => {
  const requestError = error as { code?: string; name?: string };

  return (
    requestError.code === 'ERR_CANCELED' || requestError.name === 'AbortError'
  );
};

const isResolvedRequestError = (
  response: unknown
): response is { code?: string; name?: string } => {
  if (!response || typeof response !== 'object') {
    return false;
  }

  return typeof (response as { status?: unknown }).status !== 'number';
};

const getResolvedRequestError = (response: {
  code?: string;
  name?: string;
}): Error => new Error(response.name ?? response.code ?? 'Request failed');

const TerritoriesBoard = ({
  zoom,
  bounds,
  onTerritoryClick,
}: TerritoriesBoardProps) => {
  const {
    mapTerritories,
    setFocusedTerritory,
    setSelectedTerritory,
    selectedTerritory,
    geometryLoadedIds,
    mergeMapTerritories,
    mergeGeometries,
  } = useTerritoryStore();
  const [hoveredTerritory, setHoveredTerritory] = useState<Territory | null>(
    null
  );
  const { zorTerritorySettings } = useZorTerritorySettingsStore();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pinsAbortControllerRef = useRef<AbortController | null>(null);
  const geometryAbortControllerRef = useRef<AbortController | null>(null);
  const pinsRequestIdRef = useRef(0);
  const geometryRequestIdRef = useRef(0);
  const [hasZoomedIn, setHasZoomedIn] = useState(false);

  const handleTerritoryClick = (territory: Territory) => (): void => {
    setSelectedTerritory(territory);
    setFocusedTerritory(territory);
    onTerritoryClick?.(territory);
  };

  const handleTerritoryMouseOut = (): void => {
    setHoveredTerritory(null);
  };

  const handleTerritoryMouseOver = (territory: Territory) => (): void => {
    setHoveredTerritory(territory);
  };

  // Phase 1: fetch pins only (short=true always), fires on bounds change
  const fetchTerritories = useCallback(async () => {
    if (isEmptyBounds(bounds)) {
      return;
    }

    const requestBounds = expandBounds(bounds);
    const requestId = pinsRequestIdRef.current + 1;
    const abortController = new AbortController();

    pinsRequestIdRef.current = requestId;
    pinsAbortControllerRef.current?.abort();
    pinsAbortControllerRef.current = abortController;

    try {
      const response = await apiManager.get(TERRITORIES_API_URL, {
        baseURL: configuration.TERRITORIES_API_URL,
        headers: { 'Content-Type': 'application/json' },
        signal: abortController.signal,
        params: {
          minLat: Math.floor(requestBounds.sw.lat),
          minLng: Math.floor(requestBounds.sw.lng),
          maxLat: Math.ceil(requestBounds.ne.lat),
          maxLng: Math.ceil(requestBounds.ne.lng),
          short: true,
        },
      });

      if (requestId !== pinsRequestIdRef.current) {
        return;
      }

      if (isResolvedRequestError(response)) {
        if (isRequestCanceled(response)) {
          return;
        }

        toast({
          title: 'Error loading territories',
          description:
            'Unable to connect to territory service. Please try again later.',
          variant: 'destructive',
        });
        return;
      }

      if (response.status !== 200) {
        toast({
          title: 'Error loading territories',
          description: 'Failed to load territory data for this area',
          variant: 'destructive',
        });
        return;
      }

      mergeMapTerritories(response.data.data);
    } catch (error) {
      if (isRequestCanceled(error)) {
        return;
      }

      logger.error('Failed to fetch territories', error as Error, { bounds });

      toast({
        title: 'Error loading territories',
        description:
          'Unable to connect to territory service. Please try again later.',
        variant: 'destructive',
      });
    }
  }, [bounds, mergeMapTerritories]);

  const debouncedFetchTerritories = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      fetchTerritories();
    }, 300);
  }, [fetchTerritories]);

  useEffect(() => {
    debouncedFetchTerritories();

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [debouncedFetchTerritories]);

  useEffect(() => {
    if (zoom > 7 && !hasZoomedIn) setHasZoomedIn(true);
  }, [zoom, hasZoomedIn]);

  useEffect(() => {
    return () => {
      pinsAbortControllerRef.current?.abort();
      geometryAbortControllerRef.current?.abort();
    };
  }, []);

  // Phase 2: fetch geometry for missing IDs
  useEffect(() => {
    if (!hasZoomedIn || !mapTerritories || isEmptyBounds(bounds)) return;

    const requestBounds = expandBounds(bounds);

    const missingGeomIds = mapTerritories
      .filter(
        (territory) =>
          isTerritoryInsideBounds(territory, requestBounds) &&
          !geometryLoadedIds.has(Number(territory.id))
      )
      .map((t) => Number(t.id));

    if (missingGeomIds.length === 0) return;

    const fetchGeometries = async () => {
      const requestId = geometryRequestIdRef.current + 1;
      const abortController = new AbortController();

      geometryRequestIdRef.current = requestId;
      geometryAbortControllerRef.current?.abort();
      geometryAbortControllerRef.current = abortController;

      try {
        const response = await apiManager.get(TERRITORIES_API_URL, {
          baseURL: configuration.TERRITORIES_API_URL,
          headers: { 'Content-Type': 'application/json' },
          signal: abortController.signal,
          params: {
            minLat: Math.floor(requestBounds.sw.lat),
            minLng: Math.floor(requestBounds.sw.lng),
            maxLat: Math.ceil(requestBounds.ne.lat),
            maxLng: Math.ceil(requestBounds.ne.lng),
            short: false,
          },
        });

        if (requestId !== geometryRequestIdRef.current) {
          return;
        }

        if (isResolvedRequestError(response)) {
          if (isRequestCanceled(response)) {
            return;
          }

          logger.error(
            'Error fetching territory geometries',
            getResolvedRequestError(response),
            { bounds: requestBounds }
          );
          return;
        }

        const geomData = response.data.data as Territory[];

        const geomMap = new Map<number, Territory['geom']>();
        geomData.forEach((t) => {
          if (t.geom) geomMap.set(Number(t.id), t.geom);
        });
        mergeGeometries(geomMap);
      } catch (err) {
        if (isRequestCanceled(err)) {
          return;
        }

        logger.error('Error fetching territory geometries', err as Error, {
          bounds: requestBounds,
        });
      }
    };

    void fetchGeometries();
  }, [hasZoomedIn, mapTerritories, mergeGeometries, bounds, geometryLoadedIds]);

  const renderTerritory = (territory: Territory, index: number) => {
    if (
      !territory.geom ||
      !territory.geom.coordinates ||
      !Array.isArray(territory.geom.coordinates)
    ) {
      return null;
    }

    const geom = territory.geom;
    let multiPolygonCoordinates: number[][][] = [];
    const territoryColor = getTerritoryColor(territory, zorTerritorySettings);

    if (geom.type === 'MultiPolygon') {
      multiPolygonCoordinates = geom.coordinates.map(
        (coordinates) => coordinates[0]
      );
    } else {
      multiPolygonCoordinates = [geom.coordinates[0]];
    }

    return (
      <Fragment key={`territory-${territory.id}-${index}`}>
        {multiPolygonCoordinates.map((coordinates, idx) => {
          return (
            <Polygon
              key={`${geom.type}-${idx}`}
              paths={coordinates.map(([lng, lat]) => ({
                lat,
                lng,
              }))}
              options={{
                zIndex: 20,
                fillColor: territoryColor,
                fillOpacity: hoveredTerritory?.id === territory.id ? 0.5 : 0.3,
                strokeWeight: 2,
                strokeColor: '#1a1a1a',
                strokeOpacity: 1,
              }}
              onClick={handleTerritoryClick(territory)}
              onMouseOut={handleTerritoryMouseOut}
              onMouseOver={handleTerritoryMouseOver(territory)}
            />
          );
        })}
      </Fragment>
    );
  };

  const renderTerritoryCentroid = (territory: Territory, index: number) => {
    if (
      !territory.centroid ||
      !territory.centroid.coordinates ||
      !Array.isArray(territory.centroid.coordinates)
    ) {
      return null;
    }

    return (
      <Marker
        key={`territory-centroid-${territory.id}-${index}`}
        icon={{
          url: generateMarkerSvg(
            getTerritoryColor(territory, zorTerritorySettings)
          ),
          scaledSize: new google.maps.Size(30, 37),
        }}
        position={{
          lat: territory.centroid.coordinates[1],
          lng: territory.centroid.coordinates[0],
        }}
        onClick={handleTerritoryClick(territory)}
      />
    );
  };

  return (
    <>
      {zoom > 7 &&
        mapTerritories?.map((territory, index) =>
          renderTerritory(territory, index)
        )}
      {mapTerritories?.map((territory, index) =>
        renderTerritoryCentroid(territory, index)
      )}
      {selectedTerritory && (
        <TerritoryStatsDialog
          territory={
            mapTerritories?.find(
              (t) => t.id === selectedTerritory.id
            ) as Territory
          }
          onCloseDialog={() => setSelectedTerritory(null)}
        />
      )}
    </>
  );
};

export { TerritoriesBoard };
